import type { Fetcher } from '../api/impl/utils/Fetcher';
import type { ExchangeRates } from '../common/Types';
import type DB from '../db/DB';
import SB from '../SchemaBuilder';
import { promiseTimeout } from '../utils/AsyncUtils';
import { duration } from '../utils/Utils';
import type { ServerReplicantDevOptions } from './ServerReplicant';

export type ConvertCurrencyFn = (opts: {
    fromCurrency: string;
    toCurrency: string;
    value: number;
}) => Promise<{ exchangeRate: number; value: number } | void>;

const BASE_CURRENCIES = ['USD', 'JPY'] as const;

const exchangeRatesAPIResponseSchema = SB.object({
    rates: SB.map(SB.number()),
}).additionalProperties();

function validateExchangeRatesAPIResponse(
    response: unknown,
): asserts response is SB.ExtractType<typeof exchangeRatesAPIResponseSchema> {
    exchangeRatesAPIResponseSchema.tryValidate(response);
}

export default class ServerReplicantExchangeRate {
    private cache: { rates: ExchangeRates; timestamp: number } = { timestamp: 0, rates: {} };

    constructor(
        private opts: {
            db: DB<any>;
            devOpts: ServerReplicantDevOptions;
            fetcher: Fetcher;
        },
    ) {}

    async getExchangeRates(): Promise<ExchangeRates> {
        // A game may disable this feature by not setting an access key.
        if (!this.getAccessKey()) {
            return this.cache.rates;
        }

        if (this.opts.devOpts.disableExchangeRatesLocalCache) {
            this.cache = { timestamp: 0, rates: {} };
        }

        if (this.isCacheOutdated()) {
            // Local cache is outdated.

            try {
                await this.updateCacheFromDB();

                // Add up to 10 minutes from the cache timestamp.
                // This aims to reduce the number of API calls.
                const offset = Math.floor(Math.random() * duration({ minutes: 10 }));

                if (this.isCacheOutdated(offset)) {
                    // Cache in DB is outdated.

                    await this.updateCacheFromAPI();
                }
            } catch (e: any) {
                // We don't want any errors here to disrupt the flow of the game.
                // Instead, we forward to the error reporter.
                await this.opts.devOpts.errorReporter?.captureException(e);
            }
        }

        return this.cache.rates;
    }

    convert: ConvertCurrencyFn = async (opts) => {
        const { fromCurrency, toCurrency, value } = opts;

        if (fromCurrency === toCurrency) {
            return { exchangeRate: 1, value };
        }

        const exchangeRates = await this.getExchangeRates();

        const exchangeRate = exchangeRates[toCurrency as keyof ExchangeRates]?.[fromCurrency];

        if (exchangeRate === undefined) {
            return;
        }

        return { exchangeRate, value: value / exchangeRate };
    };

    private isCacheOutdated(offset = 0) {
        const timestamp = this.cache.timestamp + offset;

        return timestamp + duration({ days: 1 }) < this.opts.devOpts.dateNow();
    }

    private async updateCacheFromDB() {
        this.cache = await this.opts.db.exchangeRates.get();
    }

    private async updateCacheFromAPI() {
        // Limit retries for the next 10 minutes, in case the external api is unresponsive.
        this.cache.timestamp += duration({ minutes: 10 });
        await this.opts.db.exchangeRates.setTimestamp(this.cache.timestamp);

        const exchangeRates: ExchangeRates = {};

        const requestPromises = BASE_CURRENCIES.map(async (baseCurrency) => {
            const response = await promiseTimeout(
                this.opts.fetcher.fetch({
                    method: 'GET',
                    url: `http://api.exchangeratesapi.io/v1/latest?access_key=${this.getAccessKey()}&base=${baseCurrency}`,
                    headers: {},
                }),
                duration({ seconds: 10 }),
            );

            const data = JSON.parse(response.body);
            validateExchangeRatesAPIResponse(data);

            exchangeRates[baseCurrency] = data.rates;
        });

        await Promise.all(requestPromises);

        this.cache = { timestamp: this.opts.devOpts.dateNow(), rates: exchangeRates };

        await this.opts.db.exchangeRates.set(this.cache);
    }

    private getAccessKey() {
        try {
            return process.env.EXCHANGE_RATES_API_ACCESS_KEY || '';
        } catch (e) {
            // In offline mode, this code gets executed in a browser.
            // There, process.env is not guaranteed to be defined.
            return '';
        }
    }
}
